import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCartStore } from '@/store/cartStore';
import { ShoppingCart } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface CartBadgeProps {
  onClick?: () => void;
  className?: string;
}

export function CartBadge({ onClick, className = '' }: CartBadgeProps) {
  const { t } = useTranslation('customer');
  const { getItemCount, total } = useCartStore();
  const badgeRef = useRef<HTMLDivElement>(null);
  const previousCountRef = useRef(getItemCount());

  const itemCount = getItemCount();
  const totalEGP = (total / 100).toFixed(1);

  // Bounce animation when item count changes
  useEffect(() => {
    if (itemCount !== previousCountRef.current && itemCount > 0 && badgeRef.current) {
      // Trigger bounce animation
      badgeRef.current.classList.add('animate-bounce');
      const timeout = setTimeout(() => {
        badgeRef.current?.classList.remove('animate-bounce');
      }, 600); // Remove after animation completes
      
      previousCountRef.current = itemCount;
      return () => clearTimeout(timeout);
    }
    previousCountRef.current = itemCount;
  }, [itemCount]);

  if (itemCount === 0) {
    return null;
  }

  return (
    <Button
      variant="outline"
      size="lg"
      onClick={onClick}
      className={`
        fixed bottom-4 left-1/2 -translate-x-1/2
        w-[calc(100%-2rem)] max-w-sm
        h-auto py-3 px-4
        flex items-center justify-between gap-4
        bg-white border-2 border-primary
        hover:bg-primary/5
        z-40
        ${className}
      `}
      aria-label={`${t('cart.title')}: ${itemCount} ${itemCount === 1 ? t('cart.item') : t('cart.items')}, ${totalEGP} EGP`}
    >
      <div className="flex items-center gap-3">
        <div className="relative" ref={badgeRef}>
          <ShoppingCart className="h-5 w-5" />
          {itemCount > 0 && (
            <Badge 
              variant="destructive"
              className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs font-bold transition-transform duration-200"
              aria-label={`${itemCount} ${itemCount === 1 ? t('cart.item') : t('cart.items')} in cart`}
            >
              {itemCount > 9 ? '9+' : itemCount}
            </Badge>
          )}
        </div>
        <div className="text-left">
          <p className="text-xs font-medium text-muted-foreground">
            {t('cart.title')}
          </p>
          <p className="text-sm font-semibold">
            {itemCount} {itemCount === 1 ? t('cart.item') : t('cart.items')}
          </p>
        </div>
      </div>

      <div className="text-right">
        <p className="text-lg font-bold text-primary">
          {totalEGP} EGP
        </p>
      </div>
    </Button>
  );
}
